import * as React from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { bindActionCreators } from 'redux';

import AutoComplete from 'antd/lib/auto-complete';
import Pagination from 'antd/lib/pagination';
import moment from 'moment';
import loadable from '@loadable/component';

import { getSearchParamsFromUrl, handleUrl } from '@common/react/utils/FIltersParamsFromUrl/FiltersParamsFromUrl';
import { ItemsProvider, useItemsProviderContext } from '@common/react/components/Core/ItemsProvider/ItemsProvider';
import ServerPageProvider from '@common/react/components/Core/ServerPageProvider/ServerPageProvider';
import FiltersComponent from '@common/react/components/UI/FiltersComponent/FiltersPropsHandler';
import { loadableDelay } from '@common/react/loadable/loadableSettings';
import Datepicker from '@common/react/components/Forms/Datepicker/Datepicker';
import Autocomplete from '@common/react/components/Forms/Autocomplete/Autocomplete';

import LogoLoader, { LogoLoaderPage } from '@commonTuna/react/components/UI/LogoLoader/LogoLoader';
import MultiSelectAuto from '@commonTuna/react/components/UI/MultiSelectAuto/MultiSelectAuto';
import { useMobileView } from '@commonTuna/react/utils/useMobileView';
import ImageWithSkeleton from '@commonTuna/react/components/UI/ImageWithSkeleton/ImageWithSkeleton';

import { Doctor } from '@app/objects/Doctor';
import DoctorCard from '@app/components/UI/DoctorCard/DoctorCard';
import { ApplicationState } from '@app/store';
import { User } from '@app/objects/User';
import { actionCreators, HeaderState } from '@app/store/HeaderSearch';
import DoctorBookedTimeBlockHeader from '@app/components/UI/DoctorBookedTimeBlock/DoctorBookedTimeBlockHeader';
import { searchFilterActionCreators, SearchFilterState } from '@app/store/SearchFilter';
import { Location } from '@app/objects/Location';
import MobileFilters from '@app/components/UI/MobileFilters/MobileFilters';

const params = {
	fallback: <LogoLoaderPage />,
};

const DoctorsFilters = loadable(() =>
	loadableDelay(import(/* webpackChunkName: "HeaderFilters" */ '@app/components/UI/Header/DoctorsFilters')), params);

export const Option = AutoComplete.Option;

interface CheckBoxItem {
	label: string;
	value: number;
}

interface Props {
	params: any;
	searchState: HeaderState;
	user: User | null;
	filterProps?: any;
}

export interface IValues extends SearchFilterState {
	text: string;
}

const getDaysCount = () => {
	if (typeof window === 'undefined') {
		return 7;
	}

	let count = Math.ceil((7 / 12) * ((window.innerWidth - 800) / 88));

	if (window.innerWidth < 1100) {
		count = Math.ceil((window.innerWidth - 350) / 88);
	}

	if (count > 7) {
		return 7;
	}

	if (window.innerWidth < 450) {
		return 2;
	}

	return count > 3 ? count : 3;
};

const filtersForClear = [
	'languageIds',
	'specialtyIds',
	'certificateIds',
	'gender',
	'globalPayerId',
];

const getPopupContainer = (node) => node.closest('.doctor-card-list') || document.body;

const DoctorsFormInner: React.FC<Props> = ({
	params,
	searchState,
	filterProps,
}) => {
	const [loadingTimes, setLoadingTimes] = React.useState(false);
	const history = useHistory();
	const isMobile = useMobileView(768);

	const dispatch = useDispatch();
	const { updateSearchFilter } = bindActionCreators(searchFilterActionCreators, dispatch);
	const searchFilterData = useSelector((state: ApplicationState) => state.searchFilterData, shallowEqual);
	const [languages, setLanguages] = React.useState<Array<CheckBoxItem>>([]);
	const [certificates, setCertificates] = React.useState<Array<CheckBoxItem>>();

	const context = useItemsProviderContext<Doctor>();

	if (!context.state) throw 'Need ItemsProvider context!';
	const {
		state: {
			filters, items, loading, pagination,
		}, actions: { handleChange, load },
	} = context;

	const [daysRange, setDaysRange] = React.useState(filters.daysRange);
	const [keys, setKeys] = React.useState<any>({});
	const { setState } = bindActionCreators(actionCreators, dispatch);
	const [values, setValues] = React.useState<IValues>({
		locationName: filters.locationId ? searchFilterData.locationName : '',
		globalPayerName: filters.globalPayerId ? searchFilterData.globalPayerName : '',
		globalProcedures: filters.globalProcedureIds ? searchFilterData.globalProcedures : [],
		text: '',
		languages: [],
		certificates: [],
	});

	const handleResize = () => {
		setDaysRange(getDaysCount());
	};

	const reloadTimes = (data) => {
		setLoadingTimes(true);
		return load(data, true)
			.then(() => {
				setLoadingTimes(false);
			});
	};

	React.useEffect(() => {
		if (JSON.stringify({ ...filters })
			!== JSON.stringify({ ...filters, ...searchState, ...params })) {
			handleChange({ ...searchState, ...params });
		}
	}, [searchState]);

	React.useEffect(() => {
		updateSearchFilter({
			...(filterProps as SearchFilterState),
		});

		window.addEventListener('resize', handleResize);
		handleResize();

		return () => window.removeEventListener('resize', handleResize);
	}, []);

	React.useEffect(() => {
		setValues((prev) => ({
			...prev,
			...searchFilterData,
		}));
		setLanguages(searchFilterData.languages?.map((value) => ({ label: value.name, value: value.id })) || []);
		setCertificates(searchFilterData.certificates?.map((value) => ({ label: value.name, value: value.id })) || []);
		setKeys({
			globalProcedureIds: Math.random(),
		});
	}, [searchFilterData]);

	React.useEffect(() => {
		if (filters.daysRange !== daysRange) {
			reloadTimes({ daysRange });
		}
	}, [filters, daysRange]);

	const onPaginationChange = (current, pageSize) => {
		handleChange({ page: current, pageSize });
	};

	const leftChevronDisabled = moment(filters.from).utc() <= moment(new Date()).utc();

	const onSelectPreviousWeek = () => {
		const prev = moment(filters.from).add(-filters.daysRange, 'days').startOf('day');
		const from = (+prev < +moment(new Date()) ? moment(new Date()).valueOf() : prev).valueOf();
		reloadTimes({ from });
	};

	const onSelectNextWeek = () => {
		reloadTimes({ from: moment(filters.from).add(filters.daysRange, 'days').startOf('day') });
	};

	const columns = items
		? items.find((item) => item?.doctorBookedTimes?.values)?.doctorBookedTimes.values
		: null;

	const goToPath = (path: string, state: any) => {
		history.push(path, state);
	};

	const onChange = (params) => {
		handleChange(params);
		setState({
			...searchState,
			...params,
		});
		handleUrl(params, history.location, history, undefined, undefined, true);
	};

	return (
		<div className="doctor-card-list">
			<div className="filters-container list-filters clearfix">
				<div className="pull-left">
					<Datepicker
						value={searchState.from ? +searchState.from : null}
						onChange={(date) => {
							let resultDate = date;
							if (date !== null) {
								resultDate = moment(date).utc().startOf('day').valueOf();
							}
							onChange({ from: resultDate });
						}}
						minDateToday
						utc
					/>
				</div>
				<div className="pull-left">
					<MultiSelectAuto
						className=""
						key={keys.globalProcedureIds || 'globalProcedureIds'}
						type="globalProcedureListRemote"
						onChange={(value) => {
							onChange({ globalProcedureIds: !value?.length ? undefined : value });
						}}
						placeholder="Procedures..."
						loadOnFocus
						antdProps={{
							defaultValue: (values?.globalProcedures || []) as any,
							className: '',
							maxTagCount: 1,
							maxTagTextLength: 15,
						}}
						items={values?.globalProcedures || [] as any}
					/>
				</div>
				<div className="pull-left ml10">
					<Autocomplete<Location>
						type="locationList"
						className="pull-left doctor-card-list-last-filter"
						renderTitle={(item: Location) => `${item.company ? `${item.company.name} - ` : ''}${item.nameEn}`}
						loadOnFocus
						onSelect={(value, option) => onChange({ locationId: +value })}
						onChange={(value = '') => {
							!value && onChange({ locationId: undefined });
						}}
						renderOption={(option) => {
							return {
								key: option.id,
								title: `${option.company ? `${option.company.name} - ` : ''}${option.nameEn}`,
								value: `${option.company ? `${option.company.name} - ` : ''}${option.nameEn}`,
								item: option,
								label: <>
									{option.avatar
										&& <div className="option-img__container">
											<ImageWithSkeleton className="option-img" src={`/remote/${option.avatar}`} />
										</div>
									}
									{`${option.company ? `${option.company.name} - ` : ''}${option.nameEn}`}
								</>,
							};
						}}
						value={values.locationName || ''}
						antdProps={{ style: { width: '100%' }, placeholder: 'Location...' }}
					/>
					<FiltersComponent
						apply
						additionalParams={searchState}
						popoverStyle={{ minWidth: '300px' }}
						getPopupContainer={getPopupContainer}
						buttonClassName="pull-right ml10"
						filtersClean={() => {
							const params = {};
							Object.values(filtersForClear).forEach((key) => params[key] = undefined);
							onChange(params);
							setValues({
								...values,
								specialties: undefined,
								globalPayerName: '',
							});
							setKeys((prev) => ({
								...prev,
								specialtyIds: Math.random(),
								globalPayer: Math.random(),
							}));
						}}
						triggerNode={isMobile ? undefined : <button type="button" className="more-filters__trigger ml10 btn btn-default">
							More Filters
						</button>}
						getChildrenList={(tooltipValues) => tooltipValues}
						tooltipValues={
							<div
								className="filters-container clearfix filters-wrapper filters-component-title__container"
								style={{ margin: 0 }}
								data-param={filtersForClear}
							>
								<DoctorsFilters
									state={searchState}
									onChange={onChange}
									keys={keys}
									values={values}
									certificates={certificates}
									languages={languages}
									getPopupContainer={getPopupContainer}
								/>
							</div>}
						customNode={isMobile ? MobileFilters : undefined}
					/>
				</div>
			</div>
			{!loading && !items.length ? <h3>No Doctors found</h3> : <>
				{columns && !loading && <div className="doctor-booked-header__fixed text-center">
					<table>
						<thead>
							<DoctorBookedTimeBlockHeader
								key={daysRange}
								daysRange={daysRange}
								disabledButtons
								leftChevronDisabled={leftChevronDisabled}
								onSelectPreviousWeek={onSelectPreviousWeek}
								onSelectNextWeek={onSelectNextWeek}
								columns={columns}
							/>
						</thead>
					</table>
					{loadingTimes && <div className="doctor-booked__loader">
						<LogoLoader size={32} />
					</div>}
				</div>}
				<div>
					{loading
						? <LogoLoaderPage key="loading" />
						: items.map((doctor: Doctor) => (
							<DoctorCard
								{...doctor}
								goToPath={goToPath}
								from={filters.from}
								daysRange={filters.daysRange}
								key={doctor.id}
							/>
						))}
				</div>
				{pagination?.total > items.length && <div className="pull-right mt10 mb10">
					<Pagination disabled={loading} onChange={onPaginationChange} onShowSizeChange={onPaginationChange} {...pagination} />
				</div>}
			</>}
		</div>
	);
};

const Doctors: React.FC = () => {
	const location = useLocation();
	const params = getSearchParamsFromUrl(location);
	const { user, searchState } = useSelector((state: ApplicationState) => ({ user: state.login.user, searchState: state.header }));

	return (
		<>
			<ServerPageProvider
				path="doctors"
				defaultTitle="Doctors"
				inner={(serverPage) =>
					<ItemsProvider<Doctor>
						type="DoctorSearchWithTimes"
						items={serverPage?.items?.list}
						pagination={{ current: 1, pageSize: 10, total: serverPage?.items.count }}
						filters={{
							...searchState,
							...params,
							from: searchState.from || moment(new Date()).valueOf(),
							daysRange: getDaysCount(),
						}}
					>
						<DoctorsFormInner
							user={user}
							searchState={searchState}
							params={params}
							filterProps={serverPage?.filterProps}
						/>
					</ItemsProvider>
				}
			/>
		</>
	);
};

export default Doctors;
